import {
  Alert,
  Button,
  DatePicker,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd"
import {
  CalendarOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  UploadOutlined,
} from "@ant-design/icons"
import { ColumnsType } from "antd/lib/table"
import { observer } from "mobx-react-lite"
import moment, { Moment } from "moment"
import { notifications } from "notifications"
import { useEffect, useState } from "react"
import { useStore } from "stores/utils/use-store"
import { getCountryNameById } from "utils/countryCodes"
import { getRegionNameByCode } from "utils/regionCodes"
import { useNavigate } from "react-router-dom"
import { ConfigItem, PixelEntry, SpecialEventsConfigType } from "domains/managingJson/store/types"
import { getGlobalImageSources } from "domains/specialEvents/normalization"
import {
  createSpecialEventExport,
  getImportTargetingWarnings,
  parseSpecialEventJson,
} from "domains/specialEvents/campaignJson"
import { classifyStoredTargeting } from "domains/specialEvents/targeting"
import Styles from "./styles.module.css"
import { gameLabel } from "domains/specialEvents/timeBasedGames"

const { Title, Text } = Typography

const DATE_TIME_FORMAT = "MMM DD, YYYY HH:mm [UTC]"
const TIMELINE_DAY_COUNT = 35
const MAX_VISIBLE_TAGS = 3

export type NotificationSrc = {
  src?: string;
}

export type NotificationSrcEntry = NotificationSrc | string | null | undefined;

export type ImagesByGeo = {
  country: string;
  imageSources?: NotificationSrcEntry[] | null;
}

export type Notification = {
  uid: string;

  enabled?: boolean;
  triggerEventName?: string;
  spawnProbability?: number;
  ingameOverlayType?: IngameOverlayType;

  trackingPixels?: PixelEntry[] | null;
  // When present, the in-game banner becomes clickable and opens this URL (http/https only).
  clickUrl?: string;
  // Fired on click, mirroring trackingPixels on impression.
  clickTrackingPixels?: PixelEntry[] | null;
  ignoreSpawnCap?: boolean;

  buffsReward?: number;
  duration?: number;
  triggerDelay?: number;
  cooldown?: number;
  excludedIsps?: string[];

  animationType?: string;
  animationDuration?: number;

  customStyles?: string;

  sourceImagesByGeo?: (ImagesByGeo | null | undefined)[] | null;
  contestPointsReward?: number;
  endgameDisplayTitle?: string;
  soundFile?: string;
  playSound?: boolean;
};

export type SpecialEventGameScreenBanner = {
  image: string;
  cta: string;
  trackingPixels?: PixelEntry[] | null;
  soundFile?: string;
  playSound?: boolean;
};

export type TargetingRuleSource = "game" | "launcher";

export type TargetingRuleMatch = "equals" | "contains";

export type TargetingRule = {
  source?: TargetingRuleSource;
  // Dotted path inside the stream's info payload, e.g. "game_info.title_mnemonic".
  path: string;
  values: string[];
  match?: TargetingRuleMatch;
}

export type SpecialEventTargeting = {
  rules: TargetingRule[];
}

export type NotificationsPerGame = {
  // Persisted as a string (the app matches it with strict `gameId === "21216"`);
  // may still be a number in-memory from the game picker until save normalizes it.
  gameId: number | string;
  notificationsSpawnCap?: number;
  stackingWrapperTopOffset?: number;
  stackingWrapperTopOffsetUnit?: "%" | "px";
  notifications?: Notification[];
  endgameBanner?: SpecialEventGameScreenBanner;
  launchGameBanner?: SpecialEventGameScreenBanner;
  // Per-game override for the "click active notification" hotkey (e.g. "Ctrl+Shift+O").
  notificationHotkey?: string;
  // All rules must pass; absent means the campaign is not mode-targeted.
  // A string only ever arrives from the raw JSON editor; this panel never writes one, but it carries
  // an existing one forward rather than dropping it and widening the campaign.
  targeting?: SpecialEventTargeting | string;
}

export type IngameOverlayType = "stacking" | "generic";

export type SpecialEvent = {
  uid: string;
  name: string;
  games: string[];
  restrictedToCountries?: string[];
  restrictedToRegions?: string[];
  excludedIsps?: string[];
  startDate: string;
  endDate: string;
  notificationsPerGame: NotificationsPerGame[];
  weight?: number;
  defaultSoundFile?: string;
  // Event-level default for the "click active notification" hotkey; used when a game has no notificationHotkey.
  notificationHotkey?: string;
  abSplit?: number;
};

type EventStatus = "draft" | "scheduled" | "live" | "ended";

type EventFilter = "all" | "live" | "scheduled" | "missingSetup";

type SetupSummary = {
  eventCount: number;
  missingAssets: number;
  missingPixels: number;
  hasMissingSetup: boolean;
  // Games the client will refuse to serve this campaign on because their targeting does not parse.
  brokenTargeting: number;
  // Games scoped to a mode, so a co-running campaign's share is not what the weights suggest.
  targetedGames: number;
}

type SpecialEventRow = SpecialEvent & {
  key: string;
  status: EventStatus;
  setup: SetupSummary;
}

const getGlobalImageCount = (notification: Notification) => getGlobalImageSources(notification).filter(source => source.src).length

const hasPixels = (pixels?: PixelEntry[] | null) => Boolean(
  pixels?.some(pixel => typeof pixel === "string" ? pixel : pixel?.src),
)

const getEventStatus = (event: SpecialEvent): EventStatus => {
  const now = moment.utc()
  const startDate = moment.utc(event.startDate)
  const endDate = moment.utc(event.endDate)

  if (!startDate.isValid() || !endDate.isValid()) return "draft"
  if (now.isBefore(startDate)) return "scheduled"
  if (now.isAfter(endDate)) return "ended"

  return "live"
}

const EMPTY_SETUP_SUMMARY: SetupSummary = {
  eventCount: 0,
  missingAssets: 0,
  missingPixels: 0,
  hasMissingSetup: false,
  brokenTargeting: 0,
  targetedGames: 0,
}

export const getEventSetup = (event: SpecialEvent): SetupSummary => {
  return event.notificationsPerGame?.reduce<SetupSummary>((summary, gameConfig) => {
    const notificationsList = gameConfig.notifications || []
    const banners = [gameConfig.launchGameBanner, gameConfig.endgameBanner].filter(Boolean) as SpecialEventGameScreenBanner[]

    const missingNotificationAssets = notificationsList.filter(notification => getGlobalImageCount(notification) === 0).length
    const missingBannerAssets = banners.filter(banner => !banner.image).length
    const missingNotificationPixels = notificationsList.filter(notification => !hasPixels(notification.trackingPixels)).length
    const missingBannerPixels = banners.filter(banner => !hasPixels(banner.trackingPixels)).length
    const targeting = classifyStoredTargeting(gameConfig.targeting)

    return {
      eventCount: summary.eventCount + notificationsList.length,
      missingAssets: summary.missingAssets + missingNotificationAssets + missingBannerAssets,
      missingPixels: summary.missingPixels + missingNotificationPixels + missingBannerPixels,
      brokenTargeting: summary.brokenTargeting + (targeting === "invalid" ? 1 : 0),
      targetedGames: summary.targetedGames + (targeting === "rules" ? 1 : 0),
      hasMissingSetup: summary.hasMissingSetup
        || missingNotificationAssets > 0
        || missingBannerAssets > 0
        || missingNotificationPixels > 0
        || missingBannerPixels > 0
        // A campaign that cannot serve at all is the most missing setup there is.
        || targeting === "invalid",
    }
  }, EMPTY_SETUP_SUMMARY) || EMPTY_SETUP_SUMMARY
}

const formatUtcDate = (date?: string) => {
  const parsedDate = moment.utc(date)

  return parsedDate.isValid() ? parsedDate.format(DATE_TIME_FORMAT) : "Not set"
}

const getTimelineDays = (calendarMonth: Moment) => {
  const startDate = calendarMonth.clone().utc().startOf("month").startOf("week")

  return Array.from({ length: TIMELINE_DAY_COUNT }, (_, index) => startDate.clone().add(index, "days"))
}

const getTimelineBarStyle = (event: SpecialEvent, timelineDays: Moment[]) => {
  const timelineStart = timelineDays[0].clone().startOf("day")
  const timelineEnd = timelineDays[timelineDays.length - 1].clone().endOf("day")
  const startDate = moment.utc(event.startDate)
  const endDate = moment.utc(event.endDate)

  if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(timelineStart) || startDate.isAfter(timelineEnd)) {
    return undefined
  }

  const startOffset = Math.max(0, startDate.clone().startOf("day").diff(timelineStart, "days"))
  const endOffset = Math.min(
    TIMELINE_DAY_COUNT - 1,
    endDate.clone().startOf("day").diff(timelineStart, "days"),
  )

  return { gridColumn: `${startOffset + 2} / ${endOffset + 3}` }
}

const statusConfig: Record<EventStatus, { label: string; className: string }> = {
  draft: { label: "Draft", className: Styles.statusDraft },
  scheduled: { label: "Scheduled", className: Styles.statusScheduled },
  live: { label: "Live", className: Styles.statusLive },
  ended: { label: "Ended", className: Styles.statusEnded },
}

export const SpecialEventsPage = observer(() => {
  const navigate = useNavigate()
  const { dataStore: { managingJsonStore } } = useStore()
  const [data, setData] = useState<SpecialEventsConfigType | undefined>()
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState<EventFilter>("all")
  const [searchValue, setSearchValue] = useState("")
  const [calendarMonth, setCalendarMonth] = useState<Moment>(moment.utc().startOf("month"))
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [importJson, setImportJson] = useState("")
  const [importError, setImportError] = useState<string>()

  useEffect(() => {
    managingJsonStore.getData()
  }, [managingJsonStore])

  useEffect(() => {
    const eventsData = managingJsonStore?.itemTypes?.filter(
      item => item.type === "specialEvents",
    ) as ConfigItem<"specialEvents">[]
    if (eventsData && eventsData.length > 0) {
      setData(eventsData[0].data)
    }
  }, [managingJsonStore.itemTypes])

  const getStoreData = (): SpecialEventsConfigType | undefined => {
    const eventsData = managingJsonStore?.itemTypes?.filter(
      item => item.type === "specialEvents",
    ) as ConfigItem<"specialEvents">[]

    return eventsData[0]?.data
  }

  const updateStore = async(
    callback: (prev: SpecialEventsConfigType | undefined) => SpecialEventsConfigType,
  ) => {
    const previousData = getStoreData()
    const newData = callback(previousData)

    previousData
      ? await managingJsonStore.update({
        name: "specialEvents",
        type: "specialEvents",
        data: newData,
      })
      : await managingJsonStore.create({
        name: "specialEvents",
        type: "specialEvents",
        data: newData,
      })
  }

  const deleteSpecialEvent = async(uid: string) => {
    managingJsonStore.funcAfterSuccess = async() => {
      notifications.success("Deleted successfully")
      await managingJsonStore.getData()
    }
    await updateStore(prev => {
      const specialEvents = prev?.specialEvents?.filter(event => event.uid !== uid)

      return { specialEvents }
    })
  }

  const edit = (event: SpecialEvent) => {
    navigate(event.uid)
  }

  const closeImport = () => {
    setIsImportOpen(false)
    setImportJson("")
    setImportError(undefined)
  }

  const createFromJson = () => {
    try {
      const importedSpecialEvent = parseSpecialEventJson(importJson)
      const hasExactOverlap = data?.specialEvents?.some(event => (
        event.name.trim().toLowerCase() === importedSpecialEvent.name.trim().toLowerCase()
        && event.startDate === importedSpecialEvent.startDate
        && event.endDate === importedSpecialEvent.endDate
      ))
      const targetingWarnings = getImportTargetingWarnings(importedSpecialEvent)
      closeImport()
      navigate("new", { state: { importedSpecialEvent } })
      if (targetingWarnings.length) {
        notifications.info(`Targeting: ${targetingWarnings.join(" ")}`, 15000)
      }
      if (hasExactOverlap) {
        notifications.info("A campaign with the same name and schedule already exists. Review before saving a duplicate.")
      }
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Could not read this campaign JSON.")
    }
  }

  const exportSpecialEvent = (event: SpecialEvent) => {
    const { fileName, json } = createSpecialEventExport(event)
    const url = window.URL.createObjectURL(new Blob([json], { type: "application/json" }))
    const link = document.createElement("a")
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const loadJsonFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      setImportJson(typeof reader.result === "string" ? reader.result : "")
      setImportError(undefined)
    }
    reader.onerror = () => setImportError("Could not read this JSON file.")
    reader.readAsText(file)
  }

  const renderTags = (
    values: string[] | undefined,
    getLabel: (value: string) => string,
    getTooltip?: (value: string) => string | undefined,
  ) => {
    const safeValues = values || []
    const visibleValues = safeValues.slice(0, MAX_VISIBLE_TAGS)
    const hiddenCount = Math.max(0, safeValues.length - visibleValues.length)

    if (safeValues.length === 0) return <Text type="secondary">Not set</Text>

    return (
      <>
        {visibleValues.map(value => (
          <Tooltip key={value} title={getTooltip?.(value)}>
            <Tag>{getLabel(value)}</Tag>
          </Tooltip>
        ))}
        {hiddenCount > 0 && <Tag>+{hiddenCount}</Tag>}
      </>
    )
  }

  const renderStatus = (status: EventStatus) => {
    const config = statusConfig[status]

    return (
      <span className={Styles.statusLabel}>
        <span className={`${Styles.statusDot} ${config.className}`} />
        {config.label}
      </span>
    )
  }

  const renderSetupTags = (setup: SetupSummary) => (
    <Space size={4} wrap>
      <Tag>{setup.eventCount} events</Tag>
      {setup.missingAssets > 0 && <Tag color="error">{setup.missingAssets} missing assets</Tag>}
      {setup.missingPixels > 0 && <Tag color="warning">{setup.missingPixels} missing pixels</Tag>}
      {setup.brokenTargeting > 0 && (
        <Tag color="error">targeting broken — not serving on {setup.brokenTargeting} games</Tag>
      )}
      {setup.targetedGames > 0 && <Tag color="processing">targeted on {setup.targetedGames} games</Tag>}
      {!setup.hasMissingSetup && <Tag color="success">Pixels ready</Tag>}
    </Space>
  )

  const columns: ColumnsType<SpecialEventRow> = [
    {
      title: "Campaign",
      dataIndex: "name",
      key: "name",
      width: "16%",
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "11%",
      render: (status: EventStatus) => renderStatus(status),
    },
    {
      title: "Games",
      dataIndex: "games",
      key: "games",
      width: "20%",
      render: (games: string[]) => renderTags(
        games,
        game => gameLabel(game) || game,
        game => `ID: ${game}`,
      ),
    },
    {
      title: "Geo",
      key: "geo",
      width: "12%",
      render: (_, record) => {
        const geoValues = [
          ...(record.restrictedToCountries || []),
          ...(record.restrictedToRegions || []),
        ]

        return renderTags(
          geoValues,
          value => value,
          value => getCountryNameById(value) || getRegionNameByCode(value),
        )
      },
    },
    {
      title: "Schedule",
      key: "schedule",
      width: "20%",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatUtcDate(record.startDate)}</Text>
          <Text>{formatUtcDate(record.endDate)}</Text>
        </Space>
      ),
    },
    {
      title: "Setup",
      dataIndex: "setup",
      key: "setup",
      width: "15%",
      render: (setup: SetupSummary) => renderSetupTags(setup),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "12%",
      render: (_, record) => (
        <Space wrap>
          <Button type="link" onClick={() => edit(record)}>
            Edit
          </Button>
          <Button type="link" icon={<DownloadOutlined />} onClick={() => exportSpecialEvent(record)}>
            Export
          </Button>
          <Popconfirm
            title="Do you really want to delete this special event?"
            placement="leftBottom"
            onConfirm={async() => {
              setConfirmLoading(true)
              try {
                await deleteSpecialEvent(record.uid)
              } finally {
                setConfirmLoading(false)
              }
            }}
            okButtonProps={{ loading: confirmLoading }}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const specialEventsWithKeys: SpecialEventRow[] = data?.specialEvents?.map(event => ({
    ...event,
    key: event.uid,
    status: getEventStatus(event),
    setup: getEventSetup(event),
  })) || []

  const normalizedSearch = searchValue.trim().toLowerCase()
  const filteredEvents = specialEventsWithKeys.filter(event => {
    const matchesSearch = !normalizedSearch || [
      event.name,
      ...(event.games || []).map(game => gameLabel(game) || game),
      ...(event.restrictedToCountries || []),
      ...(event.restrictedToRegions || []),
    ].join(" ").toLowerCase().includes(normalizedSearch)
    const matchesFilter = activeFilter === "all"
      || (activeFilter === "missingSetup" ? event.setup.hasMissingSetup : event.status === activeFilter)

    return matchesSearch && matchesFilter
  })

  const timelineDays = getTimelineDays(calendarMonth)

  return (
    <div className={Styles.page}>
      <div className={Styles.shell}>
        <div className={Styles.header}>
          <Title level={2} className={Styles.title}>Special Events</Title>
          <Space>
            <Button onClick={() => setIsImportOpen(true)}>
              Create with JSON
            </Button>
            <Button
              type="primary"
              onClick={() => {
                navigate("new")
              }}
            >
              Create from scratch
            </Button>
          </Space>
        </div>
        <div className={Styles.toolbar}>
          <Radio.Group
            value={activeFilter}
            onChange={event => setActiveFilter(event.target.value)}
            className={Styles.filterGroup}
          >
            <Radio.Button value="all">All</Radio.Button>
            <Radio.Button value="live">
              <span className={`${Styles.statusDot} ${Styles.statusLive}`} /> Live
            </Radio.Button>
            <Radio.Button value="scheduled">
              <span className={`${Styles.statusDot} ${Styles.statusScheduled}`} /> Scheduled
            </Radio.Button>
            <Radio.Button value="missingSetup">
              <span className={`${Styles.statusDot} ${Styles.statusMissing}`} /> Missing setup
            </Radio.Button>
          </Radio.Group>
          <Input.Search
            allowClear
            placeholder="Search events..."
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
            className={Styles.search}
          />
          <div className={Styles.calendarControls}>
            <Button onClick={() => setCalendarMonth(moment.utc().startOf("month"))}>Today</Button>
            <Button icon={<LeftOutlined />} onClick={() => setCalendarMonth(prev => prev.clone().subtract(1, "month"))} />
            <Button icon={<RightOutlined />} onClick={() => setCalendarMonth(prev => prev.clone().add(1, "month"))} />
            <Text strong className={Styles.monthLabel}>{calendarMonth.format("MMMM YYYY")}</Text>
            <DatePicker
              picker="month"
              allowClear={false}
              value={calendarMonth}
              suffixIcon={<CalendarOutlined />}
              onChange={value => {
                if (value) setCalendarMonth(moment.utc(value.format("YYYY-MM-01")).startOf("month"))
              }}
            />
          </div>
        </div>
        <div className={Styles.timeline}>
          <div className={Styles.timelineHeader}>
            <div />
            {timelineDays.map(day => (
              <div
                key={day.format("YYYY-MM-DD")}
                className={`${Styles.timelineDay} ${day.isSame(moment.utc(), "day") ? Styles.today : ""}`}
              >
                <Text type="secondary">{day.format("dd")}</Text>
                <Text strong={day.isSame(moment.utc(), "day")}>{day.format("D")}</Text>
              </div>
            ))}
          </div>
          <div className={Styles.timelineRows}>
            {filteredEvents.map(event => {
              const barStyle = getTimelineBarStyle(event, timelineDays)

              return (
                <div className={Styles.timelineRow} key={event.uid}>
                  <Text className={Styles.timelineName}>{event.name}</Text>
                  {timelineDays.map(day => (
                    <div
                      key={`${event.uid}-${day.format("YYYY-MM-DD")}`}
                      className={`${Styles.timelineCell} ${day.isSame(moment.utc(), "day") ? Styles.todayColumn : ""}`}
                    />
                  ))}
                  {barStyle && (
                    <div
                      className={`${Styles.timelineBar} ${event.status === "ended" ? Styles.timelineBarEnded : ""}`}
                      style={barStyle}
                    >
                      {moment.utc(event.startDate).format("MMM DD")} - {moment.utc(event.endDate).format("MMM DD")}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        <Table<SpecialEventRow>
          size="middle"
          dataSource={filteredEvents}
          columns={columns}
          pagination={false}
          className={Styles.table}
        />
        <Modal
          title="Create campaign with JSON"
          open={isImportOpen}
          okText="Review campaign"
          okButtonProps={{ disabled: !importJson.trim() }}
          width={760}
          onOk={createFromJson}
          onCancel={closeImport}
          destroyOnClose
        >
          <Space direction="vertical" size="middle" className={Styles.importModalContent}>
            <Text>
              Paste one campaign exported from this page or generated by the Moments estimator. Nothing is saved until
              you review the pre-filled form and click Save.
            </Text>
            <Upload
              accept=".json,application/json"
              maxCount={1}
              showUploadList={false}
              beforeUpload={file => {
                loadJsonFile(file as File)

                return false
              }}
            >
              <Button icon={<UploadOutlined />}>Load JSON file</Button>
            </Upload>
            {importError && <Alert type="error" showIcon message={importError} />}
            <Input.TextArea
              value={importJson}
              onChange={event => {
                setImportJson(event.target.value)
                setImportError(undefined)
              }}
              placeholder={"{\n  \"specialEvents\": [\n    { ... }\n  ]\n}"}
              autoSize={{ minRows: 14, maxRows: 24 }}
              spellCheck={false}
            />
          </Space>
        </Modal>
      </div>
    </div>
  )
})
